import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Home, MapPin, Sun, Wifi, Shield, Bath, Briefcase, TreePine, Car, ArrowRight, Check, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Maximize, Thermometer, Lightbulb, Users, Bus, Baby, ShoppingBag, Coffee, Dumbbell, GraduationCap, Heart, Sparkles, Building, Navigation, Clock, Zap, Layers, Globe, Star, Lock, Droplet, Flame, ImageOff, Image as ImageIcon, Grid3x3, Play, Package, AlertCircle, Recycle, Video, Info, Send, MessageCircle, Bed, Copy } from 'lucide-react';
import { translations, getTranslation } from './translations';
import './App.css';

// Technical plans images (separate from gallery)
const TECHNICAL_PLANS_IMAGES = [
  'plan.png',
  'Screenshot 2025-11-11 at 23.01.27.png',
  'Screenshot 2025-11-11 at 23.08.35.png',
  'Screenshot 2025-11-11 at 23.11.18.png',
  'Screenshot 2025-11-11 at 23.12.51.png',
  'Screenshot 2025-11-11 at 23.15.22.png',
  'Screenshot 2025-11-11 at 23.17.54.png'
];

// All available images from public folder
const ALL_IMAGES = [
  'IMG_20201025_233041.jpg',
  'IMG_20201027_234230.jpg',
  'IMG_20210317_215550.jpg',
  'IMG_20210903_094105.jpg',
  'IMG_20210903_094130.jpg',
  'IMG_20210929_073606.jpg',
  'IMG_20211008_134857.jpg',
  'IMG_20211008_134914.jpg',
  'IMG_20211009_120356.jpg',
  'PXL_20201113_192251478.jpg',
  'PXL_20201114_143602120.jpg',
  'PXL_20201114_161253919.jpg',
  'PXL_20201114_161309109.jpg',
  'PXL_20201206_184021223.jpg',
  'PXL_20201212_223130319.jpg',
  'PXL_20210114_070234904.jpg',
  'PXL_20210114_070314214.jpg',
  'PXL_20210114_070328486.jpg',
  'PXL_20210114_070351305.jpg',
  'PXL_20210114_070358067.jpg',
  'PXL_20210114_070409015.jpg',
  'PXL_20210114_070508177.jpg',
  'PXL_20210114_074843838.jpg',
  'PXL_20210114_080423512.jpg',
  'PXL_20210114_080447441.jpg',
  'PXL_20210516_094709207.jpg',
  'PXL_20210522_183421686.jpg',
  'PXL_20210606_155530603.jpg',
  'PXL_20210906_032419549.jpg',
  'PXL_20210908_190023795.jpg',
  'PXL_20211005_043633879.jpg',
  'PXL_20250514_092438934.MP.jpg',
  'PXL_20250514_092513335.jpg',
  'PXL_20250514_092525589.jpg',
  'PXL_20250514_092717807.jpg',
  'PXL_20250514_092736562.jpg',
  'PXL_20250514_092748245.MP.jpg',
  'PXL_20250514_092757865.MP.jpg',
  'PXL_20250514_092829665.jpg',
  'PXL_20250514_092837772.jpg',
  'PXL_20250514_092852327.MP.jpg',
  'PXL_20250514_092920874.jpg',
  'PXL_20250514_092925784.jpg',
  'PXL_20250514_092935213.MP.jpg',
  'PXL_20250514_093143954.MP.jpg',
  'PXL_20250514_093617685.jpg',
  'PXL_20250514_093623032.MP.jpg',
  'unnamed (3).png',
  'complex.webp',
  'street_view.png',
  'bench.webp',
  'bedroom.jpg',
  'new.jpg',
  'new2.jpg',
  'new3.jpg'
];

// Language detection utility
const detectLanguage = () => {
  // 1. Check URL query parameter
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang === 'ua' || urlLang === 'en') {
      return urlLang;
    }
  }

  // 2. Check saved preference in localStorage
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('apartmentLanguage');
    if (saved === 'ua' || saved === 'en') {
      return saved;
    }
  }

  // 3. Check browser language preference
  if (typeof navigator !== 'undefined') {
    const browserLanguages = navigator.languages || [navigator.language];
    for (const lang of browserLanguages) {
      const langCode = lang.toLowerCase().split('-')[0];
      if (langCode === 'uk') {
        return 'ua';
      }
      if (langCode === 'en') {
        return 'en';
      }
    }
  }

  // 4. Default to Ukrainian
  return 'ua';
};

const ApartmentLandingPage = () => {
  const TELEGRAM_URL = 'https://t.me/yuriymyk';
  // Language state with automatic detection
  const [language, setLanguage] = useState(() => {
    const detected = detectLanguage();
    // Persist detected language if it came from URL or browser (not localStorage)
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('apartmentLanguage');
      if (!saved) {
        localStorage.setItem('apartmentLanguage', detected);
      }
    }
    return detected;
  });

  // Save language preference to localStorage when changed
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('apartmentLanguage', language);
    }
  }, [language]);

  // Update <html lang> attribute for accessibility and SEO
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language === 'ua' ? 'uk' : 'en';
      document.documentElement.dir = 'ltr';
    }
  }, [language]);

  // Translation helper function
  const t = (key) => {
    return getTranslation(translations, language, key) || key;
  };

  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Load image enabled/disabled state from localStorage
  const loadImageStates = () => {
    const saved = localStorage.getItem('apartmentImageStates');
    if (saved) {
      return JSON.parse(saved);
    }
    // By default, all images are enabled
    const defaultStates = {};
    ALL_IMAGES.forEach(img => {
      defaultStates[img] = true;
    });
    return defaultStates;
  };

  const [imageStates, setImageStates] = useState(loadImageStates);
  const [galleryView, setGalleryView] = useState('grid'); // 'carousel' or 'grid'
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [visibleImagesCount, setVisibleImagesCount] = useState(20); // Initial limit for lazy loading
  
  // Load image comments from localStorage
  const loadImageComments = () => {
    const saved = localStorage.getItem('apartmentImageComments');
    if (saved) {
      return JSON.parse(saved);
    }
    return {};
  };

  const [imageComments, setImageComments] = useState(loadImageComments);
  const [editingComment, setEditingComment] = useState(null); // { imageName, line1, line2 }
  
  // Get enabled images
  const enabledImages = ALL_IMAGES.filter(img => imageStates[img] !== false);
  
  // Gallery video (joined video from public/video folder)
  const GALLERY_VIDEO = 'video/joined.mp4';
  
  // Combined gallery items: images + video at the end
  const galleryItems = [...enabledImages, { type: 'video', src: GALLERY_VIDEO }];
  
  // Get visible items (for lazy loading)
  // Insert video after first batch of images, then show more images after video
  const VIDEO_POSITION = 15; // Show video after first 15 images
  const imagesBeforeVideo = enabledImages.slice(0, Math.min(VIDEO_POSITION, enabledImages.length));
  const imagesAfterVideoStart = VIDEO_POSITION;
  const imagesAfterVideoCount = Math.max(0, visibleImagesCount - imagesBeforeVideo.length);
  const imagesAfterVideo = enabledImages.slice(imagesAfterVideoStart, imagesAfterVideoStart + imagesAfterVideoCount);
  
  const visibleItems = [];
  if (imagesBeforeVideo.length > 0) {
    visibleItems.push(...imagesBeforeVideo);
  }
  // Add video after first batch
  visibleItems.push({ type: 'video', src: GALLERY_VIDEO });
  // Add more images after video
  if (imagesAfterVideo.length > 0) {
    visibleItems.push(...imagesAfterVideo);
  }
  // Fallback: if no images, just show video
  if (visibleItems.length === 1 && visibleItems[0].type === 'video' && enabledImages.length === 0) {
    // Already handled above
  }
  
  const totalVisibleImages = imagesBeforeVideo.length + imagesAfterVideo.length;
  const hasMoreImages = enabledImages.length > totalVisibleImages;
  
  const [images, setImages] = useState(enabledImages.length > 0 ? enabledImages.slice(0, 6).map(img => `/${img}`) : []);
  const [videoUrl, setVideoUrl] = useState('/complex_video.mp4');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null); // Track index for navigation
  const [scrollY, setScrollY] = useState(0);
  // Hero underline micro-interaction
  const heroTitleRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTechnicalPlan, setCurrentTechnicalPlan] = useState(0);
  const [activeThingsToKnowTab, setActiveThingsToKnowTab] = useState('operations');
  const [expandedCards, setExpandedCards] = useState({});
  const [showContactBar, setShowContactBar] = useState(false);
  const prefersReducedMotion = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const isTouchDevice = (typeof window !== 'undefined') ? ('ontouchstart' in window || (navigator && navigator.maxTouchPoints > 0)) : false;

  const handleTiltMove = (e) => {
    if (prefersReducedMotion || isTouchDevice) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const rotX = (0.5 - y) * 6;
    const rotY = (x - 0.5) * 6;
    el.style.transform = `perspective(700px) rotateX(${rotX}deg) rotateY(${rotY}deg)`;
    el.style.transition = 'transform 80ms';
    el.style.borderColor = 'rgba(59,130,246,0.4)';
  };

  const handleTiltLeave = (e) => {
    const el = e.currentTarget;
    el.style.transform = 'perspective(700px) rotateX(0deg) rotateY(0deg)';
    el.style.transition = 'transform 150ms';
    el.style.borderColor = '';
  };

  // Subtle light sweep following mouse X for reading highlight
  const handleCardLightMove = (e) => {
    if (prefersReducedMotion || isTouchDevice) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const mx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.style.setProperty('--mx', `${(mx * 100).toFixed(2)}%`);
  };
  const handleCardLightLeave = (e) => {
    e.currentTarget.style.setProperty('--mx', '50%');
  };

  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    contactInfo: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: false
  });

  // Update images when enabled images change
  useEffect(() => {
    const enabled = ALL_IMAGES.filter(img => imageStates[img] !== false);
    if (enabled.length > 0) {
      setImages(enabled.map(img => `/${img}`));
      // Reset visible count if needed
      setVisibleImagesCount(prev => {
        if (prev > enabled.length) {
          return Math.min(20, enabled.length);
        }
        return prev;
      });
    }
  }, [imageStates]); // Only depend on imageStates, not enabledImages or visibleImagesCount

  // Reset gallery index when visible items change
  useEffect(() => {
    if (currentGalleryIndex >= visibleItems.length && visibleItems.length > 0) {
      setCurrentGalleryIndex(Math.max(0, visibleItems.length - 1));
    }
  }, [visibleItems.length, currentGalleryIndex]);

  // Define navigateLightbox before useEffects that use it
  const navigateLightbox = useCallback((direction) => {
    if (selectedImageIndex === null) return;
    const newIndex = direction === 'next' 
      ? (selectedImageIndex + 1) % enabledImages.length
      : (selectedImageIndex - 1 + enabledImages.length) % enabledImages.length;
    setSelectedImageIndex(newIndex);
    setSelectedImage(`/${enabledImages[newIndex]}`);
  }, [selectedImageIndex, enabledImages]);

  // Auto-play carousel
  useEffect(() => {
    const visibleCount = visibleItems.length;
    if (isAutoPlay && galleryView === 'carousel' && visibleCount > 1) {
      const interval = setInterval(() => {
        setCurrentGalleryIndex((prev) => (prev + 1) % visibleCount);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlay, galleryView, visibleItems.length]);

  // Auto-load more items in carousel when reaching end
  useEffect(() => {
    if (galleryView === 'carousel' && hasMoreImages && visibleItems.length > 0) {
      // When user navigates to last visible item, load more
      if (currentGalleryIndex === visibleItems.length - 1) {
        const nextCount = Math.min(visibleImagesCount + 20, enabledImages.length);
        if (nextCount > visibleImagesCount) {
          setVisibleImagesCount(nextCount);
        }
      }
    }
  }, [currentGalleryIndex, visibleItems.length, hasMoreImages, galleryView, visibleImagesCount, enabledImages.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (selectedImage && selectedImageIndex !== null) {
      const handleKeyPress = (e) => {
        if (e.key === 'ArrowLeft') {
          navigateLightbox('prev');
        } else if (e.key === 'ArrowRight') {
          navigateLightbox('next');
        } else if (e.key === 'Escape') {
          setSelectedImage(null);
          setSelectedImageIndex(null);
        }
      };
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [selectedImage, selectedImageIndex, navigateLightbox]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    const onScrollForContact = () => {
      const y = window.scrollY || 0;
      const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
      setShowContactBar(isMobile && y > 400);
    };
    window.addEventListener('scroll', onScrollForContact);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', onScrollForContact);
    };
  }, []);

  // Save image states to localStorage
  useEffect(() => {
    localStorage.setItem('apartmentImageStates', JSON.stringify(imageStates));
  }, [imageStates]);

  // Save image comments to localStorage
  useEffect(() => {
    localStorage.setItem('apartmentImageComments', JSON.stringify(imageComments));
  }, [imageComments]);

  const handleLogin = () => {
    if (password === 'admin123') {
      setIsAdmin(true);
      setPassword('');
    } else {
      alert(t('admin.login.incorrectPassword'));
    }
  };

  const toggleImageEnabled = (imageName) => {
    setImageStates(prev => ({
      ...prev,
      [imageName]: !prev[imageName]
    }));
  };

  const handleLoadMore = () => {
    // Increase visible images count (video is always included in the middle, so we just count images)
    setVisibleImagesCount(prev => Math.min(prev + 20, enabledImages.length));
  };

  const saveImageComment = (imageName, line1, line2) => {
    if (line1.trim() || line2.trim()) {
      setImageComments(prev => ({
        ...prev,
        [imageName]: { line1: line1.trim(), line2: line2.trim() }
      }));
    } else {
      // Remove comment if both lines are empty
      setImageComments(prev => {
        const newComments = { ...prev };
        delete newComments[imageName];
        return newComments;
      });
    }
    setEditingComment(null);
  };

  const deleteImageComment = (imageName) => {
    setImageComments(prev => {
      const newComments = { ...prev };
      delete newComments[imageName];
      return newComments;
    });
    setEditingComment(null);
  };

  const getImageComment = (imageName) => {
    return imageComments[imageName] || null;
  };

  // Copy file name to clipboard
  const copyFileName = async (fileName, e) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(fileName);
      // Visual feedback could be added here if needed
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Derive human-friendly alt text for accessibility
  const humanizeFileName = (name) => {
    try {
      const base = name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
      return base || 'Apartment photo';
    } catch {
      return 'Apartment photo';
    }
  };

  const getImageAlt = (imageName, index = 0) => {
    const comment = getImageComment(imageName);
    if (comment && (comment.line1 || comment.line2)) {
      const composed = [comment.line1, comment.line2].filter(Boolean).join(' — ');
      return composed;
    }
    return `Photo: ${humanizeFileName(imageName)}${index >= 0 ? ` (${index + 1})` : ''}`;
  };

  const openImageInLightbox = (imagePath, index) => {
    setSelectedImage(imagePath);
    setSelectedImageIndex(index);
  };

  const handleVideoUrlChange = (url) => {
    // Allow admin to change video file path
    setVideoUrl(url || '/complex_video.mp4');
  };

  const nextGalleryImage = useCallback(() => {
    const visibleCount = visibleItems.length;
    if (visibleCount > 0) {
      setCurrentGalleryIndex((prev) => (prev + 1) % visibleCount);
    }
  }, [visibleItems.length]);

  const prevGalleryImage = useCallback(() => {
    const visibleCount = visibleItems.length;
    if (visibleCount > 0) {
      setCurrentGalleryIndex((prev) => (prev - 1 + visibleCount) % visibleCount);
    }
  }, [visibleItems.length]);

  const goToGalleryImage = useCallback((index) => {
    const visibleCount = visibleItems.length;
    if (index >= 0 && index < visibleCount) {
      setCurrentGalleryIndex(index);
    }
  }, [visibleItems.length]);

  // Get features from translations
  const features = translations[language].features.map((feature, index) => ({
    ...feature,
    icon: index === 0 ? <Thermometer /> : index === 1 ? <Wifi /> : index === 2 ? <Shield /> : index === 3 ? <Sun /> : index === 4 ? <Briefcase /> : <Car />
  }));

  // Get neighborhood from translations
  const neighborhoodIcons = [<Baby />, <Bus />, <ShoppingBag />, <Coffee />, <TreePine />, <TreePine />, <Sparkles />, <GraduationCap />, <Heart />, <Dumbbell />];
  const neighborhood = translations[language].neighborhood.map((item, index) => ({
    ...item,
    icon: neighborhoodIcons[index]
  }));

  // Get buildingInfo from translations
  const buildingInfoIcons = [<MapPin />, <Layers />, <Home />, <Building />, <Sparkles />, <Maximize />, <Sun />, <Users />, <Layers />, <Thermometer />, <Flame />, <Building />, <Shield />, <Car />, <Zap />];
  const buildingInfo = translations[language].buildingInfo.map((item, index) => ({
    ...item,
    icon: buildingInfoIcons[index]
  }));

  // Get apartment details from translations
  const getApartmentDetails = (section) => {
    return translations[language].apartmentDetails[section] || [];
  };

  const climateHeatingDetails = getApartmentDetails('climateHeating');
  const smartHomeSecurityDetails = getApartmentDetails('smartHomeSecurity');
  const bathroomDetails = getApartmentDetails('bathroom');
  const kitchenAppliancesDetails = getApartmentDetails('kitchenAppliances');
  const interiorDesignDetails = getApartmentDetails('interiorDesign');

  // Get infrastructureSlides from translations
  const infrastructureSlides = translations[language].sections.infrastructure.slides.map((slide, index) => {
    // Use block.png for Everything You Need Nearby slide (index 0)
    if (index === 0) {
      return {
        ...slide,
        image: '/block.png'
      };
    }
    // Use map.png for City Center Access slide (index 1)
    if (index === 1) {
      return {
        ...slide,
        image: '/map.png'
      };
    }
    // Use newpoint.png for New Point Mall slide (index 2)
    if (index === 2) {
      return {
        ...slide,
        image: '/newpoint.png'
      };
    }
    // Use street_view.png for Penta Business Center slide (index 3)
    if (index === 3) {
      return {
        ...slide,
        image: '/street_view.png'
      };
    }
    // Use park700.jpg for Park 700th Anniversary slide (index 4)
    if (index === 4) {
      return {
        ...slide,
        image: '/park700.jpg'
      };
    }
    return {
      ...slide,
      image: enabledImages.length > index ? `/${enabledImages[index]}` : images[index] || ''
    };
  });

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => {
      const next = (prev + 1) % infrastructureSlides.length;
      return next;
    });
  }, [infrastructureSlides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => {
      const prevIndex = (prev - 1 + infrastructureSlides.length) % infrastructureSlides.length;
      return prevIndex;
    });
  }, [infrastructureSlides.length]);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < infrastructureSlides.length) {
      setCurrentSlide(index);
    }
  }, [infrastructureSlides.length]);

  // Technical Plans carousel navigation
  const nextTechnicalPlan = useCallback(() => {
    setCurrentTechnicalPlan((prev) => {
      const next = (prev + 1) % TECHNICAL_PLANS_IMAGES.length;
      return next;
    });
  }, []);

  const prevTechnicalPlan = useCallback(() => {
    setCurrentTechnicalPlan((prev) => {
      const prevIndex = (prev - 1 + TECHNICAL_PLANS_IMAGES.length) % TECHNICAL_PLANS_IMAGES.length;
      return prevIndex;
    });
  }, []);

  const goToTechnicalPlan = useCallback((index) => {
    if (index >= 0 && index < TECHNICAL_PLANS_IMAGES.length) {
      setCurrentTechnicalPlan(index);
    }
  }, []);

  // Contact form handler
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: false });

    const form = e.target;
    const formDataToSend = new FormData(form);
    
    // Append Web3Forms access key
    formDataToSend.append("access_key", "80eec45a-f6f3-4d19-904d-72795e8e1356");
    
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        setFormStatus({ submitting: false, success: true, error: false });
        setFormData({ name: '', contactInfo: '', message: '' });
        // Reset success message after 5 seconds
        setTimeout(() => {
          setFormStatus({ submitting: false, success: false, error: false });
        }, 5000);
      } else {
        throw new Error(data.message || 'Form submission failed');
      }
    } catch (error) {
      setFormStatus({ submitting: false, success: false, error: true });
    }
  };

  // Helper function to render accordion cards
  const renderAccordionCard = (cardKey, title, content, icon) => {
    if (!content) {
      console.error(`Content is undefined for card: ${cardKey}`);
      return null;
    }
    const isExpanded = expandedCards[cardKey] || false;
    const contentArray = Array.isArray(content) ? content : [content];
    const summary = contentArray[0] || '';
    const fullContent = contentArray;

    return (
      <div className="card-neutral p-8">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-white/5 border border-white/10">
              {React.cloneElement(icon, { className: "w-6 h-6 text-white" })}
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold mb-3 text-white">{title}</h3>
            {isExpanded ? (
              <>
                {fullContent.map((para, i) => (
                  <p key={i} className={`text-gray-300 leading-relaxed ${i === 0 ? '' : 'mt-3'}`}>
                    {para.includes('🔴') || para.includes('КРИТИЧНО') || para.includes('CRITICALLY') ? (
                      <strong className="font-bold text-white">{para}</strong>
                    ) : (
                      para
                    )}
                  </p>
                ))}
                <button
                  onClick={() => setExpandedCards({ ...expandedCards, [cardKey]: false })}
                  className="mt-4 flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors duration-300"
                >
                  <ChevronUp className="w-4 h-4" />
                  {t('sections.thingsToKnow.showLess')}
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-300 leading-relaxed">{summary}</p>
                <button
                  onClick={() => setExpandedCards({ ...expandedCards, [cardKey]: true })}
                  className="mt-4 flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors duration-300"
                >
                  {t('sections.thingsToKnow.learnMore')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Sticky Header with language segmented control */}
      <header className="fixed top-0 inset-x-0 z-40 bg-black/70 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <a href="#overview" className="text-white/90 font-semibold">{t('header.logo')}</a>
          <nav className="hidden md:flex items-center gap-6 text-sm text-gray-300">
            <a href="#overview" className="hover:text-white">{t('header.nav.overview')}</a>
            <a href="#gallery" className="hover:text-white">{t('header.nav.gallery')}</a>
            <a href="#details" className="hover:text-white">{t('header.nav.details')}</a>
            <a href="#plans" className="hover:text-white">{t('header.nav.plans')}</a>
            <a href="#contact" className="hover:text-white">{t('header.nav.contact')}</a>
          </nav>
          <div className="bg-white/10 border border-white/10 rounded-xl p-1 flex gap-1">
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1 rounded-lg text-sm ${language==='en' ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
            >EN</button>
            <button
              onClick={() => setLanguage('ua')}
              className={`px-3 py-1 rounded-lg text-sm ${language==='ua' ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
            >UA</button>
          </div>
        </div>
      </header>

      {/* Admin Login Modal */}
      {showPassword && !isAdmin && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white/10 border border-white/20 rounded-3xl p-8 max-w-sm w-full">
            <h3 className="text-2xl font-bold mb-6 text-white">{t('admin.login.title')}</h3>
            <input
              type={showPassword ? "text" : "password"}
              placeholder={t('admin.login.placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="w-full px-6 py-3 bg-white/10 border border-white/20 rounded-2xl mb-6 text-white placeholder-white/50 focus:outline-none focus:border-white/40"
            />
            <div className="flex gap-3">
              <button
                onClick={handleLogin}
                className="flex-1 bg-blue-600 text-white py-3 rounded-2xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
              >
                {t('admin.login.login')}
              </button>
              <button
                onClick={() => {setShowPassword(false); setPassword('');}}
                className="flex-1 bg-white/10 text-white py-3 rounded-2xl hover:bg-white/20 transition-all duration-300"
              >
                {t('admin.login.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="overview" className="relative min-h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 z-0"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        >
          <img
            src="/IMG_20210929_073606.jpg"
            alt={t('accessibility.apartmentOverview')}
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/90"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
          <div className="mb-6 inline-block">
            <span className="bg-white/10 text-white px-6 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 border border-white/10">
              <Sparkles className="w-4 h-4" />
              {t('hero.badge')}
            </span>
          </div>
          
          <h1
            ref={heroTitleRef}
            onMouseMove={(e) => {
              const el = heroTitleRef.current;
              if (!el) return;
              const m = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
              if (m && m.matches) return;
              const rect = el.getBoundingClientRect();
              const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
              el.style.setProperty('--x', `${(pct*100).toFixed(2)}%`);
            }}
            className="hero-underline heading-font text-6xl md:text-8xl font-black mb-6 pb-2 text-white leading-tight whitespace-pre-line"
          >
            {t('hero.title')}
          </h1>
          
          <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl mx-auto">
            {t('hero.subtitle')}
          </p>
          
          <div className="flex flex-wrap gap-3 justify-center mb-10">
            {translations[language].hero.tags.map((tag, i) => (
              <span key={i} className="bg-white/10 border border-white/20 px-5 py-2 rounded-full text-sm">
                {tag}
              </span>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('accessibility.contactViaTelegram')}
              className="magnetic group relative px-8 py-4 bg-blue-600 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30"
              onMouseMove={(e) => {
                const m = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
                if (m && m.matches) return;
                const target = e.currentTarget;
                const rect = target.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width - 0.5) * 12;
                const y = ((e.clientY - rect.top) / rect.height - 0.5) * 12;
                target.style.transform = `translate(${x}px, ${y}px)`;
              }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translate(0, 0)'; }}
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {t('mobileContact.telegram')} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </a>
            
            <button
              onClick={() => window.open('https://www.google.com/maps/place/The+Dealer+Micropub/@49.8548563,24.0155885,4075m/data=!3m1!1e3!4m6!3m5!1s0x473addc59cd279ef:0x2a9733c060df5787!8m2!3d49.8554427!4d24.0259199!16s%2Fg%2F11h58fvlh4?entry=ttu&g_ep=EgoyMDI1MTExMC4wIKXMDSoASAFQAw%3D%3D', '_blank')}
              className="px-8 py-4 bg-white/10 border border-white/20 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Navigation className="w-5 h-5" />
              {t('hero.viewOnMap')}
            </button>
          </div>
        </div>
      </section>

      {/* Mobile sticky contact bar */}
      {showContactBar && (
        <div className="md:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
          <div className="flex gap-2 bg-black/70 border border-white/10 rounded-2xl p-2">
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold"
            >{t('mobileContact.telegram')}</a>
            <a
              href="mailto:yuriy.mykhasyak@gmail.com?subject=Apartment Viewing Request"
              className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-xl text-sm font-semibold"
            >{t('mobileContact.email')}</a>
          </div>
        </div>
      )}

      {/* Footer with Admin entry */}
      <footer className="px-4 py-8 text-center text-gray-500 border-t border-white/10">
        {!isAdmin ? (
          <button
            onClick={() => setShowPassword(true)}
            className="text-gray-400 hover:text-white text-sm underline-offset-2 hover:underline"
          >
            {t('footer.admin')}
          </button>
        ) : (
          <button
            onClick={() => setIsAdmin(false)}
            className="text-gray-400 hover:text-white text-sm underline-offset-2 hover:underline"
          >
            {t('footer.exitAdmin')}
          </button>
        )}
      </footer>

      {/* Stats Bar */}
      <section className="relative z-10 -mt-20 mb-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="card-neutral p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: "81", unit: t('stats.sqm'), label: t('stats.totalArea') },
                { value: "2.8", unit: t('stats.m'), label: t('stats.ceilingHeight') },
                { value: "12+", unit: t('stats.sqm'), label: t('stats.balcony') },
                { value: "≈ 40", unit: t('stats.sqm'), label: t('stats.largeStudio') }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-black text-white">
                    {stat.value}<span className="text-xl">{stat.unit}</span>
                  </div>
                  <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="heading-font section-title text-5xl md:text-6xl font-black mb-4 text-white">
              {t('sections.modernFeatures.title')}
            </h2>
            <p className="text-xl text-gray-400">{t('sections.modernFeatures.subtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div
                key={i}
                className="group relative card-neutral p-8 transition-colors duration-200"
                onMouseMove={(e) => { handleTiltMove(e); handleCardLightMove(e); }}
                onMouseLeave={(e) => { handleTiltLeave(e); handleCardLightLeave(e); }}
              >
                <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-white/5 border border-white/10 mb-6">
                  {React.cloneElement(feature.icon, { className: "w-7 h-7 text-white" })}
                </div>
                <h3 className="card-title text-2xl font-bold mb-2 text-white">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Building & Environment */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="heading-font section-title text-5xl md:text-6xl font-black mb-4 text-white">
              {t('sections.premiumBuilding.title')}
            </h2>
            <p className="text-xl text-gray-400">{t('sections.premiumBuilding.subtitle')}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Building Info */}
            <div className="card-neutral card-static p-8">
              <h3 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <Building className="w-8 h-8 text-white/80" />
                {t('sections.premiumBuilding.buildingExcellence')}
              </h3>
              <div className="rounded-xl overflow-hidden">
                {buildingInfo.map((info, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-4 py-3 transition-colors ${
                      i % 2 === 0 ? 'bg-white/5' : 'bg-transparent'
                    } hover:bg-white/10`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-white/80">{info.icon}</span>
                      <span className="card-title text-gray-300">{info.label}</span>
                    </div>
                    <span className="font-semibold text-white text-right">{info.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 rounded-2xl border border-white/10 bg-white/5">
                <p className="text-sm text-gray-400">
                  <Star className="w-4 h-4 inline text-white/70 mr-1" />
                  {t('sections.premiumBuilding.rating')}
                </p>
              </div>
            </div>

            {/* Neighborhood */}
            <div className="card-neutral card-static p-8">
              <h3 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <MapPin className="w-8 h-8 text-white/80" />
                {t('sections.premiumBuilding.neighborhoodLife')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {neighborhood.map((item, i) => (
                  <div key={i} className="card-neutral p-4" onMouseMove={handleCardLightMove} onMouseLeave={handleCardLightLeave}>
                    <div className="flex items-start gap-3">
                      <span className="text-white/80 mt-1">{item.icon}</span>
                      <div>
                        <h4 className="font-semibold text-sm">{item.title}</h4>
                        <p className="text-xs text-gray-400 mt-1">{item.desc}</p>
                        <span className="text-xs text-white/60 font-medium mt-2 inline-block">
                          <Clock className="w-3 h-3 inline mr-1 text-white/60" />
                          {item.time}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Gallery */}
      <section id="gallery" className="relative z-10 py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
                <h2 className="heading-font section-title text-5xl md:text-6xl font-black mb-4 text-white">
                  {t('sections.visualTour.title')}
                </h2>
              <p className="text-xl text-gray-400">{t('sections.visualTour.subtitle')}</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setGalleryView(galleryView === 'carousel' ? 'grid' : 'carousel')}
                className="bg-white/10 border border-white/20 text-white px-6 py-3 rounded-2xl hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
              >
                {galleryView === 'carousel' ? <Grid3x3 className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                {galleryView === 'carousel' ? t('sections.visualTour.gridView') : t('sections.visualTour.carouselView')}
              </button>
              {galleryView === 'carousel' && galleryItems.length > 1 && (
                <button
                  onClick={() => setIsAutoPlay(!isAutoPlay)}
                  className={`px-6 py-3 rounded-2xl transition-all duration-300 flex items-center gap-2 ${
                    isAutoPlay
                      ? 'bg-green-600 text-white hover:shadow-lg hover:shadow-green-500/25' 
                      : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                  }`}
                >
                  <Play className="w-5 h-5" />
                  {isAutoPlay ? t('sections.visualTour.stopAuto') : t('sections.visualTour.autoPlay')}
                </button>
              )}
            </div>
          </div>

          {galleryItems.length === 0 ? (
            <div className="text-center py-20 card-neutral">
              <ImageOff className="w-16 h-16 mx-auto mb-4 text-gray-500" />
              <p className="text-gray-400 text-xl">{t('sections.visualTour.noImages')}</p>
            </div>
          ) : galleryView === 'carousel' ? (
            /* Carousel View */
            <div className="relative">
              <div className="relative h-[70vh] min-h-[500px] rounded-3xl overflow-hidden card-neutral mb-4">
                {visibleItems.map((item, index) => {
                  const isVideo = item.type === 'video';
                  const img = isVideo ? null : item;
                  const actualIndex = isVideo ? null : enabledImages.indexOf(img);
                  const comment = isVideo ? null : getImageComment(img);
                  return (
                    <div
                      key={isVideo ? 'video' : img}
                      className={`absolute inset-0 transition-opacity duration-700 ${
                        index === currentGalleryIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                      }`}
                    >
                      {isVideo ? (
                        <video
                          src={`/${item.src}`}
                          className="w-full h-full object-contain bg-black/20"
                          controls
                          loop
                          playsInline
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <>
                          <img
                            src={`/${img}`}
                            alt={getImageAlt(img, index)}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-contain bg-black/20"
                            onClick={() => openImageInLightbox(`/${img}`, actualIndex)}
                          />
                          {/* Comment Overlay */}
                          {comment && (comment.line1 || comment.line2) && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-4 z-20">
                              {comment.line1 && (
                                <p className="text-white text-sm md:text-base font-medium mb-1">{comment.line1}</p>
                              )}
                              {comment.line2 && (
                                <p className="text-white text-sm md:text-base">{comment.line2}</p>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
                
                {/* Navigation Arrows */}
                {visibleItems.length > 1 && (
                  <>
                    <button
                      onClick={prevGalleryImage}
                    className="absolute left-6 top-1/2 -translate-y-1/2 z-20 bg-black/50 text-white p-4 rounded-full hover:bg-black/70 transition-all duration-300 hover:scale-110"
                      aria-label={t('accessibility.previousImage')}
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextGalleryImage}
                    className="absolute right-6 top-1/2 -translate-y-1/2 z-20 bg-black/50 text-white p-4 rounded-full hover:bg-black/70 transition-all duration-300 hover:scale-110"
                      aria-label={t('accessibility.nextImage')}
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Item Counter - bottom-right corner */}
                <div className="absolute bottom-4 right-4 z-20 bg-black/50 text-white/70 px-3 py-1.5 rounded-lg text-xs font-medium">
                  {currentGalleryIndex + 1} / {visibleItems.length}
                  {hasMoreImages && (
                    <span className="ml-1 opacity-60">
                      ({visibleItems.length} of {galleryItems.length})
                    </span>
                  )}
                </div>
              </div>

              {/* Thumbnail Strip - Moved outside main image container */}
              {visibleItems.length > 1 && (
                <div className="flex gap-2 justify-center overflow-x-auto px-4 py-2">
                  {visibleItems.map((item, index) => {
                    const isVideo = item.type === 'video';
                    const img = isVideo ? null : item;
                    return (
                      <button
                        key={isVideo ? 'video' : img}
                        onClick={() => goToGalleryImage(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                          index === currentGalleryIndex 
                            ? 'border-white scale-110 shadow-lg shadow-white/50' 
                            : 'border-white/30 hover:border-white/60 opacity-70 hover:opacity-100'
                        }`}
                      >
                        {isVideo ? (
                          <div className="w-full h-full bg-black/50 flex items-center justify-center">
                            <Video className="w-8 h-8 text-white" />
                          </div>
                        ) : (
                          <img
                            src={`/${img}`}
                            alt={`Thumbnail: ${getImageAlt(img, index)}`}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Grid View - Masonry Style */
            <div>
              <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
                {visibleItems.map((item, index) => {
                  const isVideo = item.type === 'video';
                  const img = isVideo ? null : item;
                  const actualIndex = isVideo ? null : enabledImages.indexOf(img);
                  const comment = isVideo ? null : getImageComment(img);
                  // Check if this is the last image (the actual last item, which will be after the video)
                  const isLastImage = !isVideo && index === visibleItems.length - 1;
                  // Show "load more" on the last image if there are more images to load
                  const isLoadMoreItem = isLastImage && hasMoreImages;
                  const remainingCount = Math.min(20, enabledImages.length - totalVisibleImages);
                  
                  return (
                    <div
                      key={isVideo ? 'video' : img}
                      className="group relative break-inside-avoid mb-6 rounded-3xl overflow-hidden cursor-pointer card-neutral"
                      onClick={(e) => {
                        if (isLoadMoreItem) {
                          e.stopPropagation();
                          handleLoadMore();
                        } else if (!isVideo) {
                          openImageInLightbox(`/${img}`, actualIndex);
                        }
                      }}
                    >
                      {isVideo ? (
                        <video
                          src={`/${item.src}`}
                          className="w-full h-auto object-contain"
                          controls
                          loop
                          playsInline
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={`/${img}`}
                          alt={getImageAlt(img, index)}
                          className="w-full h-auto object-contain"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      
                      {/* Load More Overlay - Light and Semi-transparent */}
                      {isLoadMoreItem && (
                        <div className="absolute inset-0 bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-300 hover:bg-white/30 group-hover:bg-white/25">
                          <div className="text-center px-6 py-4">
                            <div className="text-white text-2xl md:text-3xl font-bold drop-shadow-lg">
                              {t('gallery.loadMore')} {remainingCount} {t('gallery.more')}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Comment Overlay */}
                      {comment && (comment.line1 || comment.line2) && !isLoadMoreItem && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-4">
                          {comment.line1 && (
                            <p className="text-white text-sm md:text-base font-medium mb-1">{comment.line1}</p>
                          )}
                          {comment.line2 && (
                            <p className="text-white text-sm md:text-base">{comment.line2}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Hover overlay - only show if not load more item */}
                      {/* File names hidden - commented out as requested */}
                      {/* {!isLoadMoreItem && !isVideo && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                          <div className="flex items-center gap-2 w-full">
                            <span className="text-white font-semibold text-sm font-mono truncate flex-1">{img}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyFileName(img, e);
                              }}
                              className="flex-shrink-0 p-1.5 hover:bg-white/20 rounded transition-colors"
                              title="Copy file name"
                            >
                              <Copy className="w-4 h-4 text-white" />
                            </button>
                          </div>
                        </div>
                      )} */}
                      
                      {isAdmin && !isVideo && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleImageEnabled(img);
                          }}
                          className="absolute top-4 right-4 bg-red-500/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
                          title={t('gallery.disableImage')}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Admin Image Management Panel */}
          {isAdmin && (
            <div className="mt-12 card-neutral p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <ImageIcon className="w-6 h-6" />
                {t('admin.imageManagement')} ({enabledImages.length} {t('admin.enabled')} / {ALL_IMAGES.length} {t('admin.total')})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-96 overflow-y-auto mb-6">
                {ALL_IMAGES.map((img) => {
                  const isEnabled = imageStates[img] !== false;
                  const comment = getImageComment(img);
                  return (
                    <div
                      key={img}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                        isEnabled 
                          ? 'border-green-500/50 bg-green-500/10' 
                          : 'border-red-500/50 bg-red-500/10 opacity-60'
                      }`}
                    >
                      <img
                        src={`/${img}`}
                        alt={img}
                        className="w-full h-32 object-cover"
                      />
                      {/* File Name Display */}
                      {/* File names hidden - commented out as requested */}
                      {/* <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-xs p-2 font-mono truncate flex items-center gap-2">
                        <span className="flex-1 truncate">{img}</span>
                        <button
                          onClick={(e) => copyFileName(img, e)}
                          className="flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors"
                          title="Copy file name"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div> */}
                      <button
                        onClick={() => toggleImageEnabled(img)}
                        className={`absolute inset-0 flex items-center justify-center bg-black/50 transition-all duration-300 ${
                          isEnabled ? 'opacity-0 hover:opacity-100' : 'opacity-100'
                        }`}
                      >
                        {isEnabled ? (
                          <div className="text-white text-center">
                            <ImageOff className="w-6 h-6 mx-auto mb-1" />
                            <span className="text-xs">{t('admin.disable')}</span>
                          </div>
                        ) : (
                          <div className="text-white text-center">
                            <ImageIcon className="w-6 h-6 mx-auto mb-1" />
                            <span className="text-xs">{t('admin.enable')}</span>
                          </div>
                        )}
                      </button>
                      <div className="absolute top-2 right-2 flex gap-1">
                        {isEnabled ? (
                          <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">{t('admin.on')}</div>
                        ) : (
                          <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{t('admin.off')}</div>
                        )}
                        {comment && (comment.line1 || comment.line2) && (
                          <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full" title={`${comment.line1 || ''} ${comment.line2 || ''}`.trim()}>
                            <MessageCircle className="w-3 h-3 inline" />
                          </div>
                        )}
                      </div>
                      {/* Comment Edit Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const existingComment = getImageComment(img);
                          setEditingComment({
                            imageName: img,
                            line1: existingComment?.line1 || '',
                            line2: existingComment?.line2 || ''
                          });
                        }}
                        className="absolute bottom-8 right-2 bg-blue-500/80 text-white p-1.5 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-300 z-10"
                        title={comment ? t('gallery.editComment') : t('gallery.addComment')}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
              
              {/* Comment Editor Modal */}
              {editingComment && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white/10 border border-white/20 rounded-3xl p-8 max-w-md w-full">
                    <h3 className="text-2xl font-bold mb-6 text-white">{t('gallery.editComment')}</h3>
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        {t('gallery.fileName')}
                      </label>
                      <div className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white font-mono text-sm flex items-center gap-2">
                        <span className="flex-1 truncate">{editingComment.imageName}</span>
                        <button
                          onClick={(e) => copyFileName(editingComment.imageName, e)}
                          className="flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors"
                          title="Copy file name"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        {t('gallery.commentLine1')}
                      </label>
                      <input
                        type="text"
                        value={editingComment.line1}
                        onChange={(e) => setEditingComment({ ...editingComment, line1: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/30"
                        placeholder={t('gallery.commentLine1')}
                      />
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        {t('gallery.commentLine2')}
                      </label>
                      <input
                        type="text"
                        value={editingComment.line2}
                        onChange={(e) => setEditingComment({ ...editingComment, line2: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/30"
                        placeholder={t('gallery.commentLine2')}
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => saveImageComment(editingComment.imageName, editingComment.line1, editingComment.line2)}
                        className="flex-1 bg-blue-600 text-white py-3 rounded-2xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
                      >
                        {t('gallery.saveComment')}
                      </button>
                      {getImageComment(editingComment.imageName) && (
                        <button
                          onClick={() => deleteImageComment(editingComment.imageName)}
                          className="px-6 bg-red-500/80 text-white py-3 rounded-2xl hover:bg-red-600 transition-all duration-300"
                        >
                          {t('gallery.deleteComment')}
                        </button>
                      )}
                      <button
                        onClick={() => setEditingComment(null)}
                        className="px-6 bg-white/10 text-white py-3 rounded-2xl hover:bg-white/20 transition-all duration-300"
                      >
                        {t('admin.login.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Video Section */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-font section-title text-5xl md:text-6xl font-black mb-4 text-white">
              {t('sections.aboutComplex.title')}
            </h2>
          </div>
          
          {isAdmin && (
            <div className="mb-8 flex gap-3">
              <input
                type="text"
                placeholder={t('admin.videoPlaceholder')}
                className="flex-1 px-6 py-3 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white/40"
                onBlur={(e) => handleVideoUrlChange(e.target.value)}
              />
            </div>
          )}
          
          <div className="relative aspect-video rounded-3xl overflow-hidden bg-white/5 border border-white/10">
            {videoUrl ? (
              <video
                src={videoUrl}
                className="w-full h-full object-cover"
                controls
                loop
                playsInline
                preload="metadata"
                poster="/complex.webp"
                onError={(e) => {
                  console.error('Video loading error:', e);
                }}
                onLoadedMetadata={(e) => {
                  // Ensure video starts at the beginning and loops properly
                  e.target.currentTime = 0;
                }}
                onTimeUpdate={(e) => {
                  // Prevent seeking beyond the video duration (which is already trimmed)
                  // The video is already trimmed to 5-45 seconds, so this ensures it stays within bounds
                  if (e.target.currentTime >= e.target.duration) {
                    e.target.currentTime = 0;
                  }
                }}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-gray-400">{t('admin.noVideo')}</p>
              </div>
            )}
            {/* Decorative gradient corners removed for cleaner chrome */}
          </div>
          
          {/* Video Caption */}
          <p className="text-center text-gray-400 text-sm mt-4">
            {t('sections.aboutComplex.videoCaption')}
          </p>
        </div>
      </section>

      {/* Apartment Details Section */}
      <section id="details" className="relative z-10 py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="heading-font section-title text-5xl md:text-6xl font-black mb-4 text-white">
              {t('sections.apartmentDetails.title')}
            </h2>
            <p className="text-xl text-gray-400">{t('sections.apartmentDetails.subtitle')}</p>
          </div>
          
          {/* Climate & Heating */}
          <div className="mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
              <Thermometer className="w-7 h-7 text-orange-400" />
              {t('sections.apartmentDetails.climateHeating')}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {climateHeatingDetails.map((item, index) => {
                const icons = [<Thermometer />, <Zap />, <Flame />, <Flame />];
                return (
                  <div key={index} className="card-neutral card-vstack card-equal p-6" onMouseMove={handleCardLightMove} onMouseLeave={handleCardLightLeave}>
                    <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-white/5 border border-white/10 mb-4">
                      {React.cloneElement(icons[index], { className: "w-6 h-6 text-white" })}
                    </div>
                    <h4 className="card-title text-xl font-bold mb-2 text-white">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Smart Home & Security */}
          <div className="mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
              <Shield className="w-7 h-7 text-purple-400" />
              {t('sections.apartmentDetails.smartHomeSecurity')}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {smartHomeSecurityDetails.map((item, index) => {
                const icons = [<Shield />, <Shield />, <Lock />, <Video />, <Lightbulb />, <Globe />, <Wifi />];
                return (
                  <div key={index} className="card-neutral card-vstack card-equal-tall p-6" onMouseMove={handleCardLightMove} onMouseLeave={handleCardLightLeave}>
                    <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-white/5 border border-white/10 mb-4">
                      {React.cloneElement(icons[index], { className: "w-6 h-6 text-white" })}
                    </div>
                    <h4 className="card-title text-xl font-bold mb-2 text-white">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bathroom */}
          <div className="mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
              <Bath className="w-7 h-7 text-blue-400" />
              {t('sections.apartmentDetails.bathroom')}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {bathroomDetails.map((item, index) => {
                const icons = [<Bath />, <Heart />, <Bath />];
                return (
                  <div key={index} className="card-neutral card-vstack card-equal p-6" onMouseMove={handleCardLightMove} onMouseLeave={handleCardLightLeave}>
                    <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-white/5 border border-white/10 mb-4">
                      {React.cloneElement(icons[index], { className: "w-6 h-6 text-white" })}
                    </div>
                    <h4 className="card-title text-xl font-bold mb-2 text-white">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Kitchen & Appliances */}
          <div className="mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
              <Flame className="w-7 h-7 text-orange-400" />
              {t('sections.apartmentDetails.kitchenAppliances')}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {kitchenAppliancesDetails.map((item, index) => {
                const icons = [<Flame />, <Zap />, <Droplet />, <Droplet />, <Droplet />, <Package />, <Package />];
                return (
                  <div key={index} className="card-neutral card-vstack card-equal p-6" onMouseMove={handleCardLightMove} onMouseLeave={handleCardLightLeave}>
                    <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-white/5 border border-white/10 mb-4">
                      {React.cloneElement(icons[index], { className: "w-6 h-6 text-white" })}
                    </div>
                    <h4 className="card-title text-xl font-bold mb-2 text-white">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Interior & Design */}
          <div className="mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
              <Home className="w-7 h-7 text-amber-400" />
              {t('sections.apartmentDetails.interiorDesign')}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {interiorDesignDetails.map((item, index) => {
                const icons = [<Layers />, <Home />, <Sparkles />, <Maximize />, <Zap />, <Package />, <Recycle />, <Briefcase />, <Dumbbell />, <TreePine />, <Bed />];
                return (
                  <div key={index} className="card-neutral card-vstack card-equal-tall p-6" onMouseMove={handleCardLightMove} onMouseLeave={handleCardLightLeave}>
                    <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-white/5 border border-white/10 mb-4">
                      {React.cloneElement(icons[index], { className: "w-6 h-6 text-white" })}
                    </div>
                    <h4 className="card-title text-xl font-bold mb-2 text-white">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Things to Know Section */}
      <section className="relative z-10 py-20 pb-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="heading-font section-title text-5xl md:text-6xl font-black mb-4 text-white flex items-center justify-center gap-4">
              <Info className="w-12 h-12 text-blue-400" />
              {t('sections.thingsToKnow.title')}
            </h2>
            <p className="text-xl text-gray-400">{t('sections.thingsToKnow.subtitle')}</p>
          </div>


          {/* Tabs */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-3 justify-center">
              {['operations', 'upgrades', 'unfinished'].map((tabKey) => (
                <button
                  key={tabKey}
                  onClick={() => setActiveThingsToKnowTab(tabKey)}
                  className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-300 border ${
                    activeThingsToKnowTab === tabKey
                      ? 'bg-white/10 border-white/30 text-white'
                      : 'bg-black/20 border-white/10 text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {t(`sections.thingsToKnow.tabs.${tabKey}.label`)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeThingsToKnowTab === 'operations' && (
              <>
                {renderAccordionCard(
                  'rentalApproach',
                  t('sections.thingsToKnow.tabs.operations.rentalApproach.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.operations?.rentalApproach?.content,
                  <Heart />
                )}
                {renderAccordionCard(
                  'woodenFloors',
                  t('sections.thingsToKnow.tabs.operations.woodenFloors.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.operations?.woodenFloors?.content,
                  <Layers />
                )}
                {renderAccordionCard(
                  'bathroomWater',
                  t('sections.thingsToKnow.tabs.operations.bathroomWater.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.operations?.bathroomWater?.content,
                  <Droplet />
                )}
                {renderAccordionCard(
                  'furnitureErgonomics',
                  t('sections.thingsToKnow.tabs.operations.furnitureErgonomics.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.operations?.furnitureErgonomics?.content,
                  <Home />
                )}
                {renderAccordionCard(
                  'electricalSystem',
                  t('sections.thingsToKnow.tabs.operations.electricalSystem.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.operations?.electricalSystem?.content,
                  <Lightbulb />
                )}
              </>
            )}

            {activeThingsToKnowTab === 'upgrades' && (
              <>
                {renderAccordionCard(
                  'foodDispenser',
                  t('sections.thingsToKnow.tabs.upgrades.foodDispenser.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.upgrades?.foodDispenser?.content,
                  <Recycle />
                )}
                {renderAccordionCard(
                  'washingMachine',
                  t('sections.thingsToKnow.tabs.upgrades.washingMachine.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.upgrades?.washingMachine?.content ? [translations[language].sections.thingsToKnow.tabs.upgrades.washingMachine.content] : [],
                  <Droplet />
                )}
                {renderAccordionCard(
                  'smartBlinds',
                  t('sections.thingsToKnow.tabs.upgrades.smartBlinds.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.upgrades?.smartBlinds?.content ? [translations[language].sections.thingsToKnow.tabs.upgrades.smartBlinds.content] : [],
                  <Sun />
                )}
              </>
            )}

            {activeThingsToKnowTab === 'unfinished' && (
              <>
                {renderAccordionCard(
                  'climateControl',
                  t('sections.thingsToKnow.tabs.unfinished.climateControl.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.unfinished?.climateControl?.content ? [translations[language].sections.thingsToKnow.tabs.unfinished.climateControl.content] : [],
                  <Thermometer />
                )}
                {renderAccordionCard(
                  'bedroomLights',
                  t('sections.thingsToKnow.tabs.unfinished.bedroomLights.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.unfinished?.bedroomLights?.content ? [translations[language].sections.thingsToKnow.tabs.unfinished.bedroomLights.content] : [],
                  <Lightbulb />
                )}
                {renderAccordionCard(
                  'bathroomMixer',
                  t('sections.thingsToKnow.tabs.unfinished.bathroomMixer.title'),
                  translations[language]?.sections?.thingsToKnow?.tabs?.unfinished?.bathroomMixer?.content ? [translations[language].sections.thingsToKnow.tabs.unfinished.bathroomMixer.content] : [],
                  <Bath />
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Infrastructure Slider Section */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-0 card-neutral overflow-hidden">
            {/* Left Panel - Text Content */}
            <div className="relative p-8 lg:p-12 flex flex-col justify-between h-[600px]">
              
              <div className="relative z-10">
                {/* Subtitle with icon */}
                <div className="flex items-center gap-2 mb-6 text-white/80">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm uppercase tracking-wide">{infrastructureSlides[currentSlide].subtitle}</span>
                </div>
                
                {/* Main Title */}
                <h2 className="text-3xl lg:text-4xl xl:text-5xl font-black mb-8 text-white leading-tight uppercase">
                  {infrastructureSlides[currentSlide].title}
                </h2>
                
                {/* Description */}
                <p className="text-white/90 text-lg leading-relaxed mb-8">
                  {infrastructureSlides[currentSlide].description}
                </p>
              </div>
              
              {/* Bottom Navigation */}
              <div className="relative z-10 flex items-center justify-between mt-auto pt-8 border-t border-white/10">
                <div className="flex items-center gap-4">
                  <button
                    onClick={prevSlide}
                    className="p-2 hover:bg-white/10 rounded-full transition-all duration-300"
                    aria-label={t('accessibility.previousSlide')}
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                  <span className="text-white/60 text-sm">
                    <span className="font-bold">{String(currentSlide + 1).padStart(2, '0')}</span>
                    <span className="mx-2">/</span>
                    <span>{String(infrastructureSlides.length).padStart(2, '0')}</span>
                  </span>
                  <button
                    onClick={nextSlide}
                    className="p-2 hover:bg-white/10 rounded-full transition-all duration-300"
                    aria-label={t('accessibility.nextSlide')}
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                </div>
                
                <div className="text-right">
                  <div className="text-white/60 text-xs uppercase tracking-wide mb-1">
                    {infrastructureSlides[currentSlide].location}
                  </div>
                  <div className="text-white font-semibold">
                    {infrastructureSlides[currentSlide].distance}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Panel - Image */}
            <div className="relative h-[600px] overflow-hidden">
              {infrastructureSlides.map((slide, index) => (
                <img
                  key={index}
                  src={slide.image}
                  alt={slide.title}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>
              
              {/* Slide Indicators */}
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                {infrastructureSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-white w-8' 
                        : 'bg-white/30 w-1.5 hover:bg-white/50'
                    }`}
                    aria-label={`${t('accessibility.goToSlide')} ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Plans Section */}
      <section id="plans" className="relative z-10 py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-font section-title text-5xl md:text-6xl font-black mb-4 text-white">
              {t('sections.technicalPlans.title')}
            </h2>
            <p className="text-xl text-gray-400">{t('sections.technicalPlans.subtitle')}</p>
          </div>

          <div className="card-neutral overflow-hidden">
            {/* Main Carousel */}
            <div className="relative h-[70vh] min-h-[600px] max-h-[800px] bg-black/20">
              {TECHNICAL_PLANS_IMAGES.map((img, index) => (
                <div
                  key={img}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentTechnicalPlan ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                >
                  <div className="w-full h-full overflow-hidden flex items-center justify-center">
                    <img
                      src={`/${img}`}
                      alt={`Technical Plan ${index + 1}`}
                      className={`bg-gradient-to-br from-gray-900/50 to-black/50 cursor-pointer ${
                        index === 4 
                          ? 'h-full w-auto max-w-full object-contain' 
                          : 'w-full h-full object-contain'
                      }`}
                      style={index === 4 ? { transform: 'rotate(90deg)', transformOrigin: 'center center' } : {}}
                      onClick={() => {
                        setSelectedImage(`/${img}`);
                        setSelectedImageIndex(null);
                      }}
                    />
                  </div>
                </div>
              ))}
              
              {/* Navigation Arrows */}
              {TECHNICAL_PLANS_IMAGES.length > 1 && (
                <>
                  <button
                    onClick={prevTechnicalPlan}
                    className="absolute left-6 top-1/2 -translate-y-1/2 z-20 bg-black/70 text-white p-4 rounded-full hover:bg-black/90 transition-all duration-300 hover:scale-110 shadow-lg"
                    aria-label={t('accessibility.previousPlan')}
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextTechnicalPlan}
                    className="absolute right-6 top-1/2 -translate-y-1/2 z-20 bg-black/70 text-white p-4 rounded-full hover:bg-black/90 transition-all duration-300 hover:scale-110 shadow-lg"
                    aria-label={t('accessibility.nextPlan')}
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}


              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none z-10"></div>
            </div>

            {/* Thumbnail Strip */}
            {TECHNICAL_PLANS_IMAGES.length > 1 && (
              <div className="p-6">
                <div className="flex gap-3 justify-center overflow-x-auto px-4 py-2">
                  {TECHNICAL_PLANS_IMAGES.map((img, index) => (
                    <button
                      key={img}
                      onClick={() => goToTechnicalPlan(index)}
                      className={`flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                        index === currentTechnicalPlan 
                          ? 'border-white scale-110 shadow-lg shadow-white/50 ring-2 ring-white/30' 
                          : 'border-white/30 hover:border-white/60 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={`/${img}`}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        style={index === 4 ? { transform: 'rotate(90deg)' } : {}}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-font section-title text-5xl md:text-6xl font-black mb-4 text-white">
              {t('sections.contact.title')}
            </h2>
            <p className="text-xl text-gray-400">{t('sections.contact.subtitle')}</p>
            <p className="text-sm text-gray-500 mt-2">{t('sections.contact.responseTime')}</p>
          </div>

          <div className="card-neutral p-8 md:p-12">
            <form 
              action="https://api.web3forms.com/submit" 
              method="POST"
              onSubmit={handleFormSubmit}
              className="space-y-6"
            >
              {/* Hidden fields for Web3Forms */}
              <input type="hidden" name="subject" value="Apartment Viewing Request from Website" />
              <input type="hidden" name="to" value="yuriy.mykhasyak@gmail.com" />
              <input type="hidden" name="from_name" value="Apartment Website" />
              
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('sections.contact.form.nameLabel')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:border-white/30 focus:bg-white/10 transition-all duration-300"
                  placeholder={t('sections.contact.form.namePlaceholder')}
                />
              </div>

              {/* Contact Info Field */}
              <div>
                <label htmlFor="contactInfo" className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('sections.contact.form.contactInfoLabel')}
                </label>
                <input
                  type="text"
                  id="contactInfo"
                  name="contactInfo"
                  value={formData.contactInfo}
                  onChange={handleFormChange}
                  required
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:border-white/30 focus:bg-white/10 transition-all duration-300"
                  placeholder={t('sections.contact.form.contactInfoPlaceholder')}
                />
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('sections.contact.form.messageLabel')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  required
                  rows={6}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:border-white/30 focus:bg-white/10 transition-all duration-300 resize-none"
                  placeholder={t('sections.contact.form.messagePlaceholder')}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={formStatus.submitting}
                className="group w-full px-8 py-4 bg-blue-600 rounded-2xl font-bold text-lg hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus.submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t('sections.contact.form.sending')}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    {t('sections.contact.form.submit')}
                  </>
                )}
              </button>

              {/* Success Message */}
              {formStatus.success && (
                <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-2xl flex items-center gap-3 animate-fade-in">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <p className="text-green-400 font-medium">
                    {t('sections.contact.form.success')}
                  </p>
                </div>
              )}

              {/* Error Message */}
              {formStatus.error && (
                <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-2xl flex items-center gap-3 animate-fade-in">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 font-medium">
                    {t('sections.contact.form.error')}
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="relative z-10 py-32 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="heading-font section-title text-5xl md:text-7xl font-black mb-6 text-white">
            {t('sections.cta.title')}
            <br />
            {t('sections.cta.title2')}
          </h2>
          <p className="text-xl text-gray-400 mb-4">
            {t('sections.cta.subtitle')}
          </p>
          <p className="text-lg text-gray-500 mb-12">
            {t('sections.cta.subtitle2')}
          </p>
          
          <div className="flex flex-col items-center gap-6 mb-12">
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('accessibility.contactViaTelegram')}
              className="magnetic group px-8 py-4 bg-blue-600 rounded-2xl font-bold text-lg hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 flex items-center justify-center gap-2 w-full md:w-auto"
              onMouseMove={(e) => {
                const m = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
                if (m && m.matches) return;
                const target = e.currentTarget;
                const rect = target.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width - 0.5) * 12;
                const y = ((e.clientY - rect.top) / rect.height - 0.5) * 12;
                target.style.transform = `translate(${x}px, ${y}px)`;
              }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translate(0, 0)'; }}
            >
              <MessageCircle className="w-5 h-5" />
              {t('mobileContact.telegram')}
            </a>

            {/* Telegram QR Code (responsive) */}
            <div className="flex flex-col items-center gap-4">
              <p className="text-base md:text-lg text-gray-300 mb-2">{t('sections.cta.telegram')}</p>
              <img 
                src="/QR.png" 
                alt={t('accessibility.telegramQrCode')} 
                className="w-40 h-auto md:w-64 lg:w-80 rounded-2xl border border-white/20 bg-white/5 p-3 shadow-xl"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          
          <div className="mt-16 inline-flex items-center gap-2 text-sm text-gray-500">
            <Shield className="w-4 h-4" />
            {t('sections.cta.verified')}
          </div>
        </div>
      </section>

      {/* Image Lightbox */}
      {selectedImage && selectedImageIndex !== null && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            // Close only if clicking the background, not the image or buttons
            if (e.target === e.currentTarget) {
              setSelectedImage(null);
              setSelectedImageIndex(null);
            }
          }}
        >
            <div className="relative max-w-full max-h-full flex flex-col items-center justify-center gap-4">
              <div className="relative">
                <img
                  src={selectedImage}
                alt={(() => { const name = enabledImages[selectedImageIndex]; return getImageAlt(name, selectedImageIndex) })()}
                  className="max-w-full max-h-[80vh] object-contain rounded-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            {/* Caption below media */}
            <div className="w-full max-w-5xl mx-auto px-2">
              {(() => {
                const currentImageName = enabledImages[selectedImageIndex];
                const comment = getImageComment(currentImageName);
                return comment && (comment.line1 || comment.line2) ? (
                  <div className="text-center text-white/90">
                    {comment.line1 && (
                      <p className="text-base md:text-lg font-medium">{comment.line1}</p>
                    )}
                    {comment.line2 && (
                      <p className="text-sm md:text-base text-white/80">{comment.line2}</p>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
            
            {/* Image Counter - bottom-right corner */}
            {enabledImages.length > 1 && (
              <div className="absolute bottom-4 right-4 z-20 bg-black/50 text-white/70 px-3 py-1.5 rounded-lg text-xs font-medium">
                {selectedImageIndex + 1} / {enabledImages.length}
              </div>
            )}
          </div>
          
          {/* Navigation Buttons */}
          {enabledImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('prev');
                }}
                className="absolute left-8 top-1/2 -translate-y-1/2 bg-black/50 text-white p-4 rounded-full hover:bg-black/70 transition-all duration-300 hover:scale-110 z-10"
                aria-label={t('lightbox.previous')}
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('next');
                }}
                className="absolute right-8 top-1/2 -translate-y-1/2 bg-black/50 text-white p-4 rounded-full hover:bg-black/70 transition-all duration-300 hover:scale-110 z-10"
                aria-label={t('lightbox.next')}
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          
          {/* Counter moved below media for cleaner chrome */}
          
          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage(null);
              setSelectedImageIndex(null);
            }}
            className="absolute top-8 right-8 text-white/80 hover:text-white transition-colors z-10 bg-black/50 p-3 rounded-full hover:bg-black/70"
            aria-label={t('lightbox.close')}
          >
            <X className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ApartmentLandingPage;
